import { forwardRef, useImperativeHandle } from 'react';
import { FormProvider, useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useConfirmBeforeClose } from '@/hooks/useConfirmBeforeClose';
import { SURVEY_FORM_ID } from '../../constants/survey.constants';
import { createSurveyFormSchema, type CreateSurveyFormValues } from '../../types/survey.types';
import { SurveyBasicInfoSection } from './SurveyBasicInfoSection';
import { SurveyQuestionsSection } from './SurveyQuestionsSection';

export interface SurveyFormHandle {
  reset: (values?: CreateSurveyFormValues) => void;
  requestCancel: () => void;
}

interface SurveyFormProps {
  defaultValues?: Partial<CreateSurveyFormValues>;
  onSubmit: (values: CreateSurveyFormValues, form: UseFormReturn<CreateSurveyFormValues>) => void;
  onCancel: () => void;
  isPending: boolean;
}

const FIRST_PROBLEM_SELECTOR = '[aria-invalid="true"], [role="alert"]';

const INITIAL_VALUES: CreateSurveyFormValues = {
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  questions: [],
};

export const SurveyForm = forwardRef<SurveyFormHandle, SurveyFormProps>(function SurveyForm(
  { defaultValues, onSubmit, onCancel, isPending },
  ref,
) {
  const form = useForm<CreateSurveyFormValues>({
    resolver: zodResolver(createSurveyFormSchema),
    defaultValues: { ...INITIAL_VALUES, ...defaultValues },
    mode: 'onBlur',
  });

  const isDirty = form.formState.isDirty;
  const { askBeforeClose, discardChangesDialog } = useConfirmBeforeClose(isDirty && !isPending);

  useImperativeHandle(
    ref,
    () => ({
      reset: (values) => form.reset(values),
      requestCancel: () => askBeforeClose(onCancel),
    }),
    [form, askBeforeClose, onCancel],
  );

  function handleSubmit(values: CreateSurveyFormValues) {
    onSubmit(values, form);
  }

  function handleInvalid() {
    requestAnimationFrame(() => {
      const formElement = document.getElementById(SURVEY_FORM_ID);
      const invalidEl = formElement?.querySelector<HTMLElement>(FIRST_PROBLEM_SELECTOR);
      invalidEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  return (
    <FormProvider {...form}>
      <form
        id={SURVEY_FORM_ID}
        onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
        className="flex flex-1 flex-col gap-6"
        aria-busy={isPending || undefined}
      >
        <fieldset disabled={isPending} className="contents">
          <SurveyBasicInfoSection />
          <SurveyQuestionsSection />
        </fieldset>
      </form>
      {discardChangesDialog}
    </FormProvider>
  );
});
